import { useMutation, useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';

import { LoadingPage } from '@nesso/ui/blocks/loading-page';

import {
    createPortalSessionOptions,
    paidPlansOptions,
    SESSION_GENERATE_ACTION,
    SubscriptionStatusAlert,
    subscriptionMeOptions,
    subscriptionUsageHistoryOptions,
    usageHistoryToDisplay,
    useCheckoutReturn,
    useReactivateSubscriptionOptions,
} from '@/entities/subscription';
import { useIsCancellationProcessing } from '@/features/subscription/cancel-plan';
import { PickPlan } from '@/features/subscription/pick-plan';
import { useStartCheckout } from '@/features/subscription/start-checkout';
import { useSheetStore } from '@/shared/stores/sheet-store';
import { PageTitle } from '@/shared/ui/text/page-title';
import { SettingsColumn } from '@/widgets/settings/settings-column';
import { ResubscribeSheet } from '@/widgets/subscription/resubscribe-sheet';

import { getBillingStatus } from '../model/get-billing-status';
import { useUpgradeNotice } from '../model/use-upgrade-notice';
import { ManagePlanSheet } from './plan-billing-page/manage-plan-sheet';
import { PlanBillingAlertSkeleton } from './plan-billing-page/plan-billing-alert-skeleton';
import { PlanBillingErrorAlert } from './plan-billing-page/plan-billing-error-alert';
import { PlanBillingPlanCard } from './plan-billing-page/plan-billing-plan-card';
import { PlanBillingPlanCardSkeleton } from './plan-billing-page/plan-billing-plan-card-skeleton';
import { PlanBillingSummaryCard } from './plan-billing-page/plan-billing-summary-card';
import { PlanBillingUpgradeReminderDialog } from './plan-billing-page/plan-billing-upgrade-reminder-dialog';
import { PlanBillingUsageHistoryCard } from './plan-billing-page/plan-billing-usage-history-card';

const route = getRouteApi('/settings/plan-billing');

export function PlanBillingPage() {
    const { checkout_session_id: checkoutSessionId } = route.useSearch();
    const navigate = route.useNavigate();

    const { data: subscription, status: subscriptionQueryStatus } = useQuery(
        subscriptionMeOptions(),
    );

    // nextPlanCode mirrors planCode in the steady state and diverges only once a change is
    // scheduled, so the plan catalog stays unfetched until there is an incoming plan to name.
    const hasScheduledPlanChange =
        subscription != null && subscription.nextPlanCode !== subscription.planCode;
    const paidPlansQuery = useQuery({
        ...paidPlansOptions(),
        enabled: hasScheduledPlanChange,
    });
    const paidPlans = paidPlansQuery.data;
    // Only the catalog can size the incoming plan, so hold the skeleton while it is in flight
    // rather than flashing the countless "starts" copy. A failed catalog leaves isPending
    // false, so the fallback still renders rather than stranding the page.
    const isScheduledPlanUnresolved = hasScheduledPlanChange && paidPlansQuery.isPending;

    const usageHistoryQuery = useQuery(subscriptionUsageHistoryOptions());
    const usageHistory = usageHistoryQuery.data
        ? usageHistoryToDisplay(usageHistoryQuery.data)
        : null;

    const clearCheckoutParam = () =>
        navigate({
            search: (prev) => ({ ...prev, checkout_session_id: undefined }),
            replace: true,
        });

    const { isWaitingCheckout, isCheckoutSettled, isResubscribeSettling } = useCheckoutReturn(
        checkoutSessionId,
        clearCheckoutParam,
    );

    const upgradeNotice = useUpgradeNotice(checkoutSessionId);

    const { selectPlan, pendingPlanCode } = useStartCheckout();

    const openSheet = useSheetStore((state) => state.openSheet);
    const handleManagePlan = () =>
        openSheet({ type: 'subscription/manage-plan', label: 'Manage plan' });
    const handleResubscribe = () =>
        openSheet({ type: 'subscription/resubscribe', label: 'Choose plan' });

    const portalSession = useMutation(createPortalSessionOptions());
    const handleBillingDetails = () => portalSession.mutate(undefined);
    const handleUpdatePaymentMethod = () => portalSession.mutate('payment-method');

    const reactivate = useMutation(useReactivateSubscriptionOptions());
    const handleReactivate = () => reactivate.mutate();

    const isCancellationProcessing = useIsCancellationProcessing();

    if (portalSession.isPending || portalSession.isSuccess || isResubscribeSettling) {
        return <LoadingPage className="fixed inset-0 z-50" />;
    }

    const isPending =
        (isWaitingCheckout && !isCheckoutSettled) ||
        isCancellationProcessing ||
        isScheduledPlanUnresolved ||
        subscriptionQueryStatus === 'pending';
    // A failed refetch keeps the last subscription in the cache and still reports `error`, so the
    // query's own status has to be read: showing a stale plan with live controls is worse than
    // saying the page could not load.
    const isError = !isPending && (subscriptionQueryStatus === 'error' || subscription == null);
    const readySubscription = isPending || isError ? undefined : subscription;

    // The catalog has loaded by now, so a missing plan means it genuinely does not list the
    // incoming code. Its own null allowance is a plan with no cap, which the footer must not
    // confuse with not knowing the plan's size.
    const scheduledPlan = hasScheduledPlanChange
        ? paidPlans?.find((plan) => plan.code === readySubscription?.nextPlanCode)
        : undefined;
    const scheduledPlanSessions = scheduledPlan
        ? (scheduledPlan.includedSessions ?? 'unlimited')
        : null;

    const billingStatus = readySubscription ? getBillingStatus(readySubscription) : null;
    // A trial has no billing to summarise, so it gets the plan picker — and the picker needs a
    // wider column than the rest of the page.
    const showsPlanPicker = readySubscription != null && billingStatus === null;
    // Absent on a plan with no session cap: there is no allowance to count against, so the plan
    // card is dropped rather than rendered empty.
    const sessionLimit = readySubscription?.limits[SESSION_GENERATE_ACTION];
    const isPlanRunning = billingStatus === 'active' || billingStatus === 'cancelling';

    return (
        <>
            <SettingsColumn className={showsPlanPicker ? 'max-w-5xl' : undefined}>
                <PageTitle className="mb-8">Nesso plan</PageTitle>

                {isPending && (
                    <>
                        <PlanBillingAlertSkeleton />
                        <PlanBillingPlanCardSkeleton />
                    </>
                )}

                {isError && (
                    <>
                        <PlanBillingErrorAlert onBillingDetails={handleBillingDetails} />
                        <PlanBillingPlanCard
                            sessionsUsed={0}
                            sessionsIncluded={0}
                            overageRateCents={0}
                            onManagePlan={handleManagePlan}
                            isError
                        />
                    </>
                )}

                {readySubscription && billingStatus && (
                    <PlanBillingSummaryCard
                        status={billingStatus}
                        subscription={readySubscription}
                        scheduledPlanSessions={scheduledPlanSessions}
                        isReactivating={reactivate.isPending || reactivate.isSuccess}
                        onInvoiceHistory={handleBillingDetails}
                        onEditPaymentMethod={handleUpdatePaymentMethod}
                        onManagePlan={handleManagePlan}
                        onReactivate={handleReactivate}
                        onChoosePlan={handleResubscribe}
                    />
                )}

                {readySubscription && isPlanRunning && sessionLimit && (
                    <PlanBillingPlanCard
                        sessionsUsed={readySubscription.counters[SESSION_GENERATE_ACTION] ?? 0}
                        sessionsIncluded={sessionLimit.limit}
                        overageRateCents={sessionLimit.overagePriceCents}
                        onManagePlan={handleManagePlan}
                        // Manage Plan lives in the summary card's footer on a running plan,
                        // and nothing is manageable until a scheduled cancellation is undone.
                        manageHidden={billingStatus === 'active'}
                        manageDisabled={billingStatus === 'cancelling'}
                    />
                )}

                {isPlanRunning && (
                    <PlanBillingUsageHistoryCard
                        items={usageHistory?.showCard ? usageHistory.items : []}
                        showTimeFilter={usageHistory?.showTimeFilter ?? false}
                    />
                )}

                {readySubscription && showsPlanPicker && (
                    <div className="flex flex-col gap-8">
                        {readySubscription.status === 'CANCELED' && (
                            <SubscriptionStatusAlert
                                variant="expired"
                                retentionEndsAt={readySubscription.retentionEndsAt}
                            />
                        )}
                        <PickPlan pendingPlanCode={pendingPlanCode} onSelectPlan={selectPlan} />
                    </div>
                )}
            </SettingsColumn>
            <ManagePlanSheet />
            <ResubscribeSheet returnTo="/settings/plan-billing" />
            <PlanBillingUpgradeReminderDialog
                open={upgradeNotice.isOpen}
                email={upgradeNotice.email}
                onClose={upgradeNotice.dismiss}
            />
        </>
    );
}
